import TicketsResource from 'flow/api/omnitickets';
import Actions from 'flow/actions/index';
import { List } from 'immutable';
import { networkErrorDispatcher } from 'flow/actions/app-actions';
import {
    applyTicketMembersChange,
    fetchTicket,
} from 'flow/actions/tickets-action';
import {
    selectTicketIsFetched,
    selectTicketIsOnScreen,
    selectTicketsKeysForTabMounted,
    selectTicketViewIsMounted,
    selectTicketVisibility,
} from 'flow/selectors/TicketsSelectors';
import {
    getEntityKeyNextKey,
    ticketFetchErrorMeansNoAccess,
    ticketRequestError,
    ticketWriteWasRefused,
} from 'flow/utils/ticket-utils';
import { matchPath } from 'react-router-dom';
import { history } from 'flow/configure-store';
import { generatePath } from 'flow/utils/router-utils';
import { routes } from 'flow/types/routes';
import type { AppDispatch } from 'flow/types/Redux';
import type { State } from 'flow/types/State';
import type { ExtensionKey, TicketKey } from 'flow/types/EntityKeys';
import type { SuggestedTagDTO } from 'flow/types/Tag';
import type { Ticket } from 'flow/types/Tickets';

export const addTicketMembers =
    (ticketKey: TicketKey, extensionKeys: List<ExtensionKey>) =>
    (dispatch: AppDispatch) => {
        return new Promise((resolve, reject) => {
            TicketsResource.addMembers(
                {
                    onResult(data: Ticket) {
                        dispatch(onTicketReceived(ticketKey, data));
                        resolve(data);
                    },
                    onClientError: (response?: Response) => {
                        dispatch(onTicketWriteRefused(ticketKey, response));
                        reject(ticketRequestError(response));
                    },
                    onError: (response?: Response) => {
                        networkErrorDispatcher(dispatch);
                        reject(new Error(`${response?.status}`));
                    },
                },
                ticketKey,
                extensionKeys,
            );
        });
    };

export const generateSuggestedTags =
    (ticketKey: TicketKey, transcriptOnly?: boolean) =>
    (dispatch: AppDispatch) => {
        return new Promise((resolve, reject) => {
            TicketsResource.generateSuggestedTags(
                {
                    onResult(json: SuggestedTagDTO[]) {
                        resolve(json);
                    },
                    onClientError: (error?: Response) => {
                        reject(ticketRequestError(error));
                    },
                    onError: (error?: Response) => {
                        networkErrorDispatcher(dispatch);
                        reject(new Error(`${error?.status}`));
                    },
                },
                ticketKey,
                transcriptOnly ? { filter: 'TRANSCRIPT' } : {},
            );
        });
    };

const onTicketReceived =
    (ticketKey: TicketKey, ticketDTO: Ticket) => (dispatch: AppDispatch) => {
        dispatch({
            type: Actions.UPDATE_TICKET,
            ticketKey,
            ticketDTO,
        });
        dispatch({
            type: Actions.UPDATE_TICKET_TEMP_SEARCH_DATA,
            ticketKey,
            ticketDTO,
        });
    };

/**
 * Brings the ticket up to date after the backend refused a write on it.
 *
 * Only a 403 says the write itself was not allowed. omni-ticket-service refuses a
 * write it may not even admit exists with a 404, and every other status says
 * nothing about access, so both are left alone.
 *
 * The refusal means the stored viewerPermissions is stale — it was resolved when
 * the ticket was last fetched, and the access level has changed since. A 200
 * replaces it, so the controls the backend now refuses stop being offered.
 *
 * A refused fetch says the access level moved far enough that the ticket is not
 * readable either, so it goes. Nothing navigates: the user did not leave this
 * ticket, and a mounted view answers the drop with the missing-access state.
 *
 * Telling the user is not this thunk's job. useTicketAction does that, from where
 * the user's action was started.
 */
export const onTicketWriteRefused =
    (ticketKey: TicketKey, request?: { status?: number }) =>
    (dispatch: AppDispatch) => {
        if (!ticketWriteWasRefused(request)) {
            return;
        }

        dispatch(settleReadAccess(ticketKey, false));
    };

// Tickets with a read-access refetch in flight, from an own removal or a refused
// write. The local DELETE and the push it causes both land in the acting client,
// and two overlapping GETs apply in completion order — a late 200 would resurrect
// a ticket an earlier 404 evicted.
//
// The two callers can disagree on removalPerformedByOwnUser, and the one already
// in flight wins. So leaving a ticket while a refused write is still being settled
// drops it without moving the user on, leaving them on the missing-access state
// instead of the next ticket. It takes a self-removal inside that request's window
// and lands on the answer a colleague's removal gets, which is why the flag is not
// tracked per fetch.
const readAccessRefetchesInFlight = new Set<TicketKey>();

/**
 * Moves the user on from a ticket they have just left, to the next one in the
 * mounted tab.
 *
 * Only the caller that performed the removal navigates, so being removed by a
 * colleague leaves the user where they are and the refused refetch shows them
 * the missing-access state instead. That keeps one behaviour across the ticket
 * page, the overview popover and the PBX detail page tab, and says what
 * happened rather than relocating them without explanation.
 */
const navigateOnLeavingTicket =
    (ticketKey: TicketKey) =>
    (_dispatch: AppDispatch, getState: () => State) => {
        const state = getState();

        if (!selectTicketViewIsMounted(state, ticketKey)) {
            //Ticket view for ticket removed not open, do nothing
            return;
        }

        // A ticket view is not only the tickets route — the overview popover and
        // the PBX detail page mount one too, and more places may later. What
        // follows navigates within the tickets route, so from anywhere else it
        // would throw the user off the page they are on.
        const ticketsRouteIsActive = !!matchPath(history.location.pathname, {
            path: routes['tickets'],
        });
        if (!ticketsRouteIsActive) {
            return;
        }

        const ticketKeyToNavigateTo = getEntityKeyNextKey(
            selectTicketsKeysForTabMounted(state),
            ticketKey,
        );
        if (!ticketKeyToNavigateTo) {
            history.push(routes['tickets']);
        } else {
            history.push(
                generatePath(routes['tickets.ticketKey'], {
                    ticketKey: ticketKeyToNavigateTo,
                }),
            );
        }
    };

/**
 * Drops the ticket, and moves the user on if they are the one who left it.
 *
 * Navigation belongs here rather than to the caller, because it should only
 * happen when the ticket actually goes: leaving a Restricted or Public ticket
 * keeps it, and staying put is the point. Both reports of our own removal — the
 * DELETE response and the push it causes — carry removalPerformedByOwnUser, so
 * whichever drops the ticket navigates and the other returns before reaching
 * here, whatever order they arrive in.
 */
const dropTicketOnAccessLost =
    (ticketKey: TicketKey, removalPerformedByOwnUser: boolean) =>
    (dispatch: AppDispatch) => {
        if (removalPerformedByOwnUser) {
            dispatch(navigateOnLeavingTicket(ticketKey));
        }

        dispatch({
            type: Actions.REMOVE_TICKET,
            ticketKey,
        });
    };

/**
 * Asks the backend whether the ticket is still readable and acts on the answer.
 * A 200 carries a freshly resolved DTO, so the ticket stays and its members,
 * access level and viewer permissions are all replaced with what the backend now
 * says. A failure is judged by ticketFetchErrorMeansNoAccess.
 *
 * Erring towards keeping is deliberate: a ticket the user can no longer read is
 * corrected the next time a ticket view mounts, whereas one dropped by mistake
 * stays invisible until the whole list is fetched again.
 *
 * Both an own removal and a refused write end up here, asking the same question
 * of the same ticket, so they share the in-flight guard rather than racing each
 * other. They differ only in whether the drop moves the user on.
 */
const settleReadAccess =
    (ticketKey: TicketKey, removalPerformedByOwnUser: boolean) =>
    (dispatch: AppDispatch) => {
        if (readAccessRefetchesInFlight.has(ticketKey)) {
            return;
        }
        readAccessRefetchesInFlight.add(ticketKey);

        dispatch(fetchTicket(ticketKey))
            .catch((error?: Response) => {
                if (!ticketFetchErrorMeansNoAccess(error)) {
                    return;
                }
                dispatch(
                    dropTicketOnAccessLost(
                        ticketKey,
                        removalPerformedByOwnUser,
                    ),
                );
            })
            .finally(() => {
                readAccessRefetchesInFlight.delete(ticketKey);
            });
    };

export const onOwnUserRemovedFromTicket =
    (
        ticketKey: TicketKey,
        removalPerformedByOwnUser: boolean,
        membersAdded: ExtensionKey[],
        membersRemoved: ExtensionKey[],
    ) =>
    (dispatch: AppDispatch, getState: () => State) => {
        const state = getState();
        if (!selectTicketIsFetched(state, ticketKey)) {
            // The other report of this same removal got here first and removed
            // the ticket. Nothing left to do.
            return;
        }

        dispatch(
            applyTicketMembersChange(ticketKey, membersAdded, membersRemoved),
        );

        const ticketIsOnScreen = selectTicketIsOnScreen(state, ticketKey);

        // Losing membership no longer settles whether the ticket stays readable:
        // the restricted and public access levels keep a non-member in. Only the
        // backend can answer — the push carries no access level, none is pushed
        // when it changes, and the stored viewerPermissions was resolved while the
        // user was still a member.
        //
        // So the stored access level decides only where asking is not worth it: our
        // own removal, which is a deliberate act that should respond at once, and a
        // ticket that is nowhere on screen, where a later mount or list fetch
        // corrects it. Everywhere else nothing happens until the backend answers,
        // so the ticket is never removed and then put back.
        const accessLevelDecides =
            removalPerformedByOwnUser || !ticketIsOnScreen;

        if (
            accessLevelDecides &&
            selectTicketVisibility(state, ticketKey) === 'private'
        ) {
            dispatch(
                dropTicketOnAccessLost(ticketKey, removalPerformedByOwnUser),
            );
            return;
        }

        if (ticketIsOnScreen) {
            dispatch(settleReadAccess(ticketKey, removalPerformedByOwnUser));
        }
    };
